/**
 * THE MANIFEST AGGREGATOR — one place that resolves a deployment to its product.
 *
 * <p>Resolution order, and the order is the decision:
 *
 * <ol>
 *   <li><b>the ROLE</b>, if an administrator — Admin carries no domains, so an operator on any
 *       host gets the Studio. Checked first, because a role is a property of the person and a
 *       hostname is a property of the URL, and the person outranks the URL;</li>
 *   <li><b>the HOSTNAME</b>, matched against each manifest's own domain list;</li>
 *   <li><b>{@code REACT_APP_PRODUCT_TARGET}</b>, baked at build time;</li>
 *   <li><b>{@code productTarget.json}</b>, the committed file the deploy script already reads;</li>
 *   <li><b>astro</b>, which is what {@code localhost} gets.</li>
 * </ol>
 *
 * <h2>⚠ WHY THE FALLBACK IS A PRODUCT AND NOT A THROW</h2>
 *
 * An unknown host must still render a chart. A throw here takes the application down for a
 * hostname nobody added to a list — a staging URL, a preview channel, an IP address — and the
 * symptom is a blank page rather than a product with the wrong name in its header. Falling back
 * is visible and recoverable; failing is neither.
 */

import astroManifest from './astro.manifest';
import integraldeltaManifest from './integraldelta.manifest';
import dhanabhyasManifest from './dhanabhyas.manifest';
import adminManifest from './admin.manifest';
import { PRODUCT_TARGETS } from '../productTarget';
import { toolForAction, isToolAction } from '../toolRegistry';

export {
  astroManifest, integraldeltaManifest, dhanabhyasManifest, adminManifest,
};

/**
 * Every manifest, by id.
 *
 * ⚠ KEYED BY THE EXISTING PRODUCT TARGET IDS — `astro`, `integraldelta`, `dhanabhyas`, `admin`.
 * Those strings are already the contract: they are what `productTarget.json` holds, what
 * `REACT_APP_PRODUCT_TARGET` carries, and what the admin override stores. A manifest id that
 * differed from them would need a translation table, and a translation table is the place the
 * two vocabularies drift. A test asserts this set equals `PRODUCT_TARGETS` exactly.
 */
export const MANIFESTS = Object.freeze({
  astro: astroManifest,
  integraldelta: integraldeltaManifest,
  dhanabhyas: dhanabhyasManifest,
  admin: adminManifest,
});

/** The product-target ids, re-exported so a consumer needs one import. */
export { PRODUCT_TARGETS };

export const MANIFEST_IDS = Object.freeze(Object.keys(MANIFESTS));

/** The product served when nothing else resolves. */
export const DEFAULT_MANIFEST_ID = 'astro';

/** A manifest by id, or undefined. Never throws. */
export const manifestById = (id) => MANIFESTS[id];

/**
 * The CUSTOMER manifests, in resolution order.
 *
 * <p>Admin is excluded by construction rather than by a filter on its id: it declares no
 * domains, so it can never match a hostname. The list is explicit anyway, because "admin has no
 * domains" is a fact about that file and a reader of this one should not have to go and check.
 */
const BY_HOSTNAME = Object.freeze([
  dhanabhyasManifest, astroManifest, integraldeltaManifest,
]);

/**
 * Normalise a hostname for comparison.
 *
 * <p>Lower-cased, port stripped, trailing dot stripped. A port matters because a dev server is
 * reached as {@code localhost:3000} and {@code window.location.hostname} does not include one —
 * but a caller passing {@code window.location.host} is an easy mistake and this absorbs it.
 */
const normalise = (hostname) => String(hostname || '')
  .toLowerCase()
  .trim()
  .replace(/:\d+$/, '')
  .replace(/\.$/, '');

/**
 * The manifest a hostname serves, or undefined when none claims it.
 *
 * <p>⚠ An EXACT match on the domain list, never a suffix test. A suffix rule
 * ({@code endsWith('integraldelta.ai')}) matches every subdomain on the internet that ends that
 * way, including one a third party controls — the same reason the Firebase browser key's
 * referrer list refuses bare wildcards. A new subdomain is one line in the manifest.
 */
export const getManifestByHostname = (hostname) => {
  const host = normalise(hostname);
  if (!host) return undefined;
  return BY_HOSTNAME.find((m) => m.domains.includes(host));
};

/**
 * The build-time product target, from the environment or the committed file.
 *
 * <p>Both are consulted because both already exist: the deploy script writes
 * {@code productTarget.json} and CI sets {@code REACT_APP_PRODUCT_TARGET}. The env var wins, so
 * a one-off build can override the committed default without editing a tracked file.
 */
export const getConfiguredManifest = () => {
  const fromEnv = process.env.REACT_APP_PRODUCT_TARGET;
  if (fromEnv && MANIFESTS[fromEnv]) return MANIFESTS[fromEnv];

  /*
   * ⚠ REQUIRED, not imported at the top. `productTarget.json` is a committed file that a deploy
   * script rewrites, and a missing or malformed one must not be a build failure — so the read is
   * wrapped. Webpack resolves the path statically either way.
   */
  try {
    // eslint-disable-next-line global-require
    const target = require('../productTarget.json');
    const id = target && target.activeProduct;
    if (id && MANIFESTS[id]) return MANIFESTS[id];
  } catch (err) {
    /* No file, or not JSON. Fall through to the default. */
  }
  return undefined;
};

/**
 * The manifest a session opens on.
 *
 * @param hostname {@code window.location.hostname}
 * @param isAdmin  whether the signed-in user is an administrator
 * @param previewId an admin's saved preview override, honoured only for an administrator
 */
export const resolveInitialManifest = (hostname, isAdmin = false, previewId = null) => {
  /*
   * ⚠ THE PREVIEW IS CHECKED BEFORE THE ROLE AND ONLY FOR AN ADMIN. An operator who left the
   * dropdown on `astro` must come back to `astro` — a preview that reset on reload is a control
   * whose effect cannot be inspected for longer than one page load. A non-admin's stored value
   * is ignored outright rather than cleared: a customer cannot grant themselves the Studio by
   * writing to their own localStorage.
   */
  if (isAdmin && previewId && MANIFESTS[previewId]) return MANIFESTS[previewId];
  if (isAdmin) return adminManifest;

  return getManifestByHostname(hostname)
    || getConfiguredManifest()
    || MANIFESTS[DEFAULT_MANIFEST_ID];
};


/* ── PANEL HELPERS ──────────────────────────────────────────────────────────────────────────── */

/**
 * Every tool id a manifest's panel names, across every tab and group.
 *
 * <p>Flat, because almost every question about the panel is about MEMBERSHIP — is this tool in it,
 * does it collide with the toolbar, is it reachable — and only the renderer cares about the
 * nesting. Returns an empty array for a customer manifest, whose `adminPanel` is null.
 */
export const panelToolIdsOf = (manifest) => {
  if (!manifest || !manifest.adminPanel) return [];
  return manifest.adminPanel.tabs.flatMap((t) => t.groups.flatMap((g) => g.tools));
};

/** Every tool id a manifest names anywhere — the toolbar and the panel together. */
export const allToolIdsOf = (manifest) => [
  ...((manifest && manifest.toolbar) || []),
  ...panelToolIdsOf(manifest),
];

/** One tab's declaration, or undefined. */
export const panelTabOf = (manifest, tabId) => (manifest && manifest.adminPanel
  ? manifest.adminPanel.tabs.find((t) => t.id === tabId)
  : undefined);


/**
 * WHETHER A PRODUCT CARRIES THE TOOL BEHIND {@code actionId} — the command gate's product axis.
 *
 * <h2>⚠ THIS REPLACED `PRODUCT_DENIES`, AND THE REPLACEMENT HAD TO HAPPEN IN THE SAME EDIT</h2>
 *
 * The gate's own note said "one deny list governs the toolbar and the Copilot together", and that
 * stopped being true the moment the toolbar became manifest-driven: the bar hid what no manifest
 * named while the gate refused what `PRODUCT_DENIES` named, so the two could disagree about any
 * tool added after Stage 1. Deleting the deny list without this would not have been a cleanup —
 * it would have WIDENED what the Copilot may dispatch, silently.
 *
 * <p>⚠ A NON-TOOL ACTION IS ALWAYS ALLOWED, and that is what makes an allow-list usable here. The
 * Copilot dispatches plenty that is not a toolbar tool — symbol changes, navigation, drawing
 * modes — and a manifest has never heard of any of it. Refusing by absence would break every one
 * of those commands; the gate's other four axes govern them.
 *
 * <p>An unknown product is ALLOWED for the same reason the hostname fallback returns a product
 * rather than throwing: a staging target nobody listed must not refuse every command.
 */
export const isToolAvailableForProduct = (actionId, product) => {
  if (!actionId) return true;
  if (!isToolAction(actionId)) return true;
  const manifest = MANIFESTS[product];
  if (!manifest) return true;
  const tool = toolForAction(actionId);
  return allToolIdsOf(manifest).includes(tool.id);
};

export default MANIFESTS;
